import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSync } from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../context/UserContext';  // Añadir esta línea

const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

const FactoresArriendo = () => {
    const { currentUser } = useContext(UserContext);
    const [añosDisponibles, setAñosDisponibles] = useState([new Date().getFullYear()]); // Inicializar con año actual
    const [año, setAño] = useState(new Date().getFullYear()); // Default año actual
    const [factores, setFactores] = useState(
        meses.reduce((acc, mes) => ({ ...acc, [mes]: '0.000' }), {})
    );
    const [loading, setLoading] = useState(true);
    const [focusedInput, setFocusedInput] = useState(null);

    const getAuthHeader = () => {
        const token = localStorage.getItem('token');
        return {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    };

    const fetchAñosDisponibles = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/factores-arriendo/anios`,
                {
                    headers: {
                        'Authorization': `Bearer ${currentUser?.token}`
                    }
                }
            );
            const añosArray = response.data;
            if (añosArray.length > 0) {
                setAñosDisponibles(añosArray);
            } else {
                // Si no hay años en la BD, usar el año actual
                setAñosDisponibles([new Date().getFullYear()]);
            }
        } catch (error) {
            console.error('Error al obtener años:', error);
            // En caso de error, mantener el año actual
            setAñosDisponibles([new Date().getFullYear()]);
        }
    };

   
    const fetchFactores = async (selectedAño) => {
        if (!selectedAño) return;
       
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/factores-arriendo/year/${selectedAño}`,
                getAuthHeader()
            );

            const defaultFactores = meses.reduce((acc, mes) => ({
                ...acc,
                [mes]: '0.000'
            }), {});
    
            if (response.data && Array.isArray(response.data)) {
                response.data.forEach(factor => {
                    if (factor.mes && factor.factor !== undefined) {
                        defaultFactores[factor.mes] = factor.factor.toFixed(3);
                    }
                });
            }
    
            setFactores(defaultFactores);
        } catch (error) {
            console.error('Error al cargar factores:', error);
            setFactores(meses.reduce((acc, mes) => ({ ...acc, [mes]: '0.000' }), {}));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No token in localStorage');
            return;
        }

        if (!año) {
            console.log('No año available');
            return;
        }

        fetchFactores(año);
    }, [año]);

    

    const handleNuevaApertura = async () => {
        const { value: nuevoAño } = await Swal.fire({
            title: 'Apertura de Nuevo Año',
            html: `
                <div class="mb-4">
                    <p class="text-sm text-gray-600 mb-2">
                        Seleccione el año para el cual desea crear los factores
                    </p>
                    <select id="yearSelect" class="w-full p-2 border rounded">
                        ${Array.from({ length: 3 }, (_, i) => {
                            const year = new Date().getFullYear() + i;
                            return `<option value="${year}">${year}</option>`;
                        }).join('')}
                    </select>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Crear',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                return document.getElementById('yearSelect').value;
            }
        });
    
        if (nuevoAño) {
            const añoInt = parseInt(nuevoAño);
            setAñosDisponibles(prev => [...new Set([...prev, añoInt])]);
            setAño(añoInt);
            // Inicializar factores para el nuevo año
            setFactores(meses.reduce((acc, mes) => ({ ...acc, [mes]: '0.000' }), {}));
        }
    };


    const handleFactorChange = (mes, valor) => {
        const regex = /^\d*\.?\d{0,3}$/;
        if (valor === '' || regex.test(valor)) {
            setFactores(prev => ({
                ...prev,
                [mes]: valor
            }));
        }
    };

    const handleFocus = (mes) => {
        setFocusedInput(mes);
        if (factores[mes] === '0.000') {
            setFactores(prev => ({
                ...prev,
                [mes]: ''
            }));
        }
    };

    const handleBlur = (mes) => {
        setFocusedInput(null);
        if (factores[mes] === '') {
            setFactores(prev => ({
                ...prev,
                [mes]: '0.000'
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const factoresArray = Object.entries(factores).map(([mes, factor]) => ({
            mes,
            factor: parseFloat(factor) || 0
        }));

        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/factores-arriendo`,
                {
                    año,
                    factores: factoresArray
                },
                getAuthHeader()
            );

            Swal.fire({
                icon: 'success',
                title: 'Éxito',
                text: 'Factores guardados correctamente'
            });
        } catch (error) {
            console.error('Error al guardar:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'Error al guardar los factores'
            });
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center space-x-4">
                        <h2 className="text-2xl font-bold">Factores de Actualización</h2>
                        {añosDisponibles.length > 0 && (
                            <select
                                value={año}
                                onChange={(e) => setAño(Number(e.target.value))}
                                className="ml-4 border border-gray-300 rounded-md shadow-sm py-2 px-3"
                            >
                                {añosDisponibles.map(a => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <button
                        onClick={handleNuevaApertura}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        <FontAwesomeIcon icon={faSync} className="mr-2" />
                        Nueva Apertura
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2">Cargando factores...</p>
                </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-2">
                            {meses.map((mes) => (
                                <div key={mes} className="flex items-center space-x-4">
                                    <label className="w-32 text-sm font-medium text-gray-700 capitalize">
                                        {mes}
                                    </label>
                                    <input
                                        type="text"
                                        value={factores[mes]}
                                        onChange={(e) => handleFactorChange(mes, e.target.value)}
                                        onFocus={() => handleFocus(mes)}
                                        onBlur={() => handleBlur(mes)}
                                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-right"
                                        placeholder="0.000"
                                        pattern="^\d*\.?\d{0,3}$"
                                        required
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                type="submit"
                                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <FontAwesomeIcon icon={faSave} className="mr-2" />
                                Guardar Factores
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default FactoresArriendo;